import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  Tabs,
  Tab,
  FormGroup,
  Checkbox,
  Grid,
  Tooltip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CloudUpload,
  Description,
  GetApp,
  CheckCircle,
  Label,
  Assignment,
  QrCode,
  Print,
  Clear,
  Visibility
} from '@mui/icons-material';
import { useActivityLog } from '../context/ActivityLogContext';
import API_BASE_URL, { apiCall } from '../config/api';
import { formatForFilename, formatLogTime } from '../utils/dateUtils';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const LabelPrinting = () => {
  const theme = useTheme();
  const {
    addActivityProcess,
    updateActivityProcess,
    addLog: contextAddLog,
    completeActivityProcess,
  } = useActivityLog();

  const upcQrFileInputRef = useRef(null);
  const customRugFileInputRef = useRef(null);

  const [selectedTab, setSelectedTab] = useState(0);
  const [upcQrFile, setUpcQrFile] = useState(null);
  const [customRugFile, setCustomRugFile] = useState(null);
  const [labelType, setLabelType] = useState('QR');
  const [addVendorIdToQR, setAddVendorIdToQR] = useState(false);
  const [customRugSizes, setCustomRugSizes] = useState({
    '2X3': false,
    '3X5': false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [results, setResults] = useState(null);
  const [preview, setPreview] = useState(null);   // { url, fileName, labelType }
  const previewFrameRef = useRef(null);
  const [isDraggingUpc, setIsDraggingUpc] = useState(false);
  const [isDraggingRug, setIsDraggingRug] = useState(false);

  const addLog = (processId, message) => {
    const formattedMessage = `[${formatLogTime()}] ${message}`;
    contextAddLog(processId, formattedMessage);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setError('');
    setSuccess('');
    setResults(null);
  };

  const validateFile = (file) => {
    if (!file.name.toLowerCase().match(/\.(xlsx|xls|csv)$/)) {
      setError('Please select a valid Excel (.xlsx, .xls) or CSV (.csv) file.');
      return false;
    }
    return true;
  };

  const handleUpcQrFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && validateFile(file)) {
      setUpcQrFile(file);
      setError('');
      setSuccess('');
      setResults(null);
    }
  };

  const handleCustomRugFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && validateFile(file)) {
      setCustomRugFile(file);
      setError('');
      setSuccess('');
      setResults(null);
    }
  };

  const handleCustomRugSizeChange = (size) => {
    setCustomRugSizes(prev => ({
      ...prev,
      [size]: !prev[size]
    }));
  };

  const handleGenerateUpcQrLabels = async (mode = 'print') => {
    if (!upcQrFile) {
      setError('Please select a file first.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setResults(null);

    const activityProcessId = `label-printing-${Date.now()}`;
    const labelTypeDisplay = labelType === 'QR' ? 'QR Code' : labelType === 'Barcode' ? 'UPC-A Barcode' : 'No Code';
    const actionLabel = mode === 'print' ? 'Print' : mode === 'preview' ? 'Preview' : 'Download';
    addActivityProcess(activityProcessId, `Label Printing (${labelTypeDisplay}) - ${actionLabel}`, 'Label Printing');
    addLog(activityProcessId, `Starting ${labelTypeDisplay} label generation for ${actionLabel.toLowerCase()}...`);
    addLog(activityProcessId, `File: ${upcQrFile.name}`);
    updateActivityProcess(activityProcessId, { progressPercentage: 10 });

    try {
      const formData = new FormData();
      formData.append('file', upcQrFile);
      formData.append('labelType', labelType);
      formData.append('addVendorId', addVendorIdToQR.toString());

      addLog(activityProcessId, `Uploading file to server...`);
      updateActivityProcess(activityProcessId, { progressPercentage: 30 });

      const response = await fetch(`${API_BASE_URL}/api/LabelPrinting/generate-upc-qr-labels`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        },
        body: formData
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please log out and log back in.');
        }
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Label generation failed');
      }

      addLog(activityProcessId, `Generating labels...`);
      updateActivityProcess(activityProcessId, { progressPercentage: 70 });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const timestamp = formatForFilename();
      const fileName = `UPC_QR_Labels_${labelType}_${timestamp}.pdf`;

      if (mode === 'preview') {
        // Shown in a dialog rather than sent to a printer or saved, so the same
        // generated file can then be printed or downloaded from there without
        // being built a second time.
        setPreview({ url, fileName, labelType: labelTypeDisplay });
        addLog(activityProcessId, `Preview opened - ${fileName}`);
      } else if (mode === 'print') {
        // Use hidden iframe to trigger browser's print dialog (includes printer selection)
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        iframe.src = url;
        document.body.appendChild(iframe);
        iframe.onload = () => {
          setTimeout(() => {
            try {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
            } catch (e) {
              // Fallback: open in new tab if iframe print fails
              window.open(url, '_blank');
            }
          }, 500);
        };
        addLog(activityProcessId, `Print dialog opened - select your printer`);
        // Clean up iframe and blob URL after user is done (2 min delay)
        setTimeout(() => {
          if (document.body.contains(iframe)) document.body.removeChild(iframe);
          window.URL.revokeObjectURL(url);
        }, 120000);
      } else {
        // Download mode
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        addLog(activityProcessId, `PDF downloaded: ${fileName}`);
      }

      updateActivityProcess(activityProcessId, { progressPercentage: 100 });
      addLog(activityProcessId, `Processing completed successfully!`);
      completeActivityProcess(activityProcessId, true);

      const successMsg = mode === 'print'
        ? `${labelTypeDisplay} labels generated and opened for printing!`
        : mode === 'preview'
          ? `${labelTypeDisplay} labels generated - check the preview before printing.`
          : `${labelTypeDisplay} labels successfully generated and downloaded!`;
      setSuccess(successMsg);
      setResults({ success: true, labelType: labelTypeDisplay, fileName, mode });

    } catch (err) {
      console.error('Generation error:', err);
      addLog(activityProcessId, `Error: ${err.message}`);
      completeActivityProcess(activityProcessId, false);
      setError(err.message || 'An error occurred during label generation.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCustomRugLabels = async () => {
    if (!customRugFile) {
      setError('Please select a file first.');
      return;
    }

    const selectedSizes = Object.keys(customRugSizes).filter(size => customRugSizes[size]);
    if (selectedSizes.length === 0) {
      setError('Please select at least one label size.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setResults(null);

    const activityProcessId = `label-printing-rug-${Date.now()}`;
    addActivityProcess(activityProcessId, `Custom Rug Labels (${selectedSizes.join(', ')})`, 'Label Printing');
    addLog(activityProcessId, `Starting custom rug label generation...`);
    addLog(activityProcessId, `File: ${customRugFile.name}`);
    addLog(activityProcessId, `Sizes: ${selectedSizes.join(', ')}`);
    updateActivityProcess(activityProcessId, { progressPercentage: 10 });

    try {
      const formData = new FormData();
      formData.append('file', customRugFile);
      formData.append('labelSizes', selectedSizes.join(','));

      addLog(activityProcessId, `Uploading file to server...`);
      updateActivityProcess(activityProcessId, { progressPercentage: 30 });

      const response = await fetch(`${API_BASE_URL}/api/LabelPrinting/generate-custom-rug-labels`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        },
        body: formData
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please log out and log back in.');
        }
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Custom rug label generation failed');
      }

      addLog(activityProcessId, `Generating labels...`);
      updateActivityProcess(activityProcessId, { progressPercentage: 70 });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = formatForFilename();
      const fileName = `Custom_Rug_Labels_${timestamp}.zip`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      updateActivityProcess(activityProcessId, { progressPercentage: 100 });
      addLog(activityProcessId, `File downloaded: ${fileName}`);
      addLog(activityProcessId, `Processing completed successfully!`);
      completeActivityProcess(activityProcessId, true);

      setSuccess(`Custom rug labels successfully generated and downloaded!`);
      setResults({ success: true, labelType: 'Custom Rug', fileName });

    } catch (err) {
      console.error('Generation error:', err);
      addLog(activityProcessId, `Error: ${err.message}`);
      completeActivityProcess(activityProcessId, false);
      setError(err.message || 'An error occurred during label generation.');
    } finally {
      setLoading(false);
    }
  };

  const downloadUpcQrTemplate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/LabelPrinting/download-upc-qr-template`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SKU_Labels_Template_${formatForFilename()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download template. Please try again.');
    }
  };

  const downloadCustomRugTemplate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/LabelPrinting/download-custom-rug-template`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Custom_Rug_Template_${formatForFilename()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download template. Please try again.');
    }
  };

  // The generated PDF, held open for the preview dialog. The object URL stays
  // alive until the dialog closes, so it is revoked there rather than straight
  // after creation the way the print and download paths do it.
  const closePreview = () => {
    setPreview((p) => {
      if (p) window.URL.revokeObjectURL(p.url);
      return null;
    });
  };

  // Print and download act on the file already on screen, so a preview that
  // looks right is exactly what goes to the printer.
  const printPreview = () => {
    const frame = previewFrameRef.current;
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch (e) {
      // Cross-origin or a viewer that blocks it: fall back to a new tab.
      if (preview) window.open(preview.url, '_blank');
    }
  };

  const downloadPreview = () => {
    if (!preview) return;
    const link = document.createElement('a');
    link.href = preview.url;
    link.setAttribute('download', preview.fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const resetForm = () => {
    closePreview();
    setUpcQrFile(null);
    setCustomRugFile(null);
    setLabelType('QR');
    setAddVendorIdToQR(false);
    setCustomRugSizes({ '2X3': false, '3X5': false });
    setError('');
    setSuccess('');
    setResults(null);
    if (upcQrFileInputRef.current) upcQrFileInputRef.current.value = '';
    if (customRugFileInputRef.current) customRugFileInputRef.current.value = '';
  };

  const clearUpcFile = () => {
    setUpcQrFile(null);
    if (upcQrFileInputRef.current) upcQrFileInputRef.current.value = '';
  };

  const clearRugFile = () => {
    setCustomRugFile(null);
    if (customRugFileInputRef.current) customRugFileInputRef.current.value = '';
  };

  // Drag and drop handlers for UPC/QR
  const handleDragEnterUpc = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingUpc(true); };
  const handleDragLeaveUpc = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingUpc(false); };
  const handleDragOverUpc = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDropUpc = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDraggingUpc(false);
    const file = e.dataTransfer.files[0];
    if (file && validateFile(file)) { setUpcQrFile(file); setError(''); }
  };

  // Drag and drop handlers for Custom Rug
  const handleDragEnterRug = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingRug(true); };
  const handleDragLeaveRug = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingRug(false); };
  const handleDragOverRug = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDropRug = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDraggingRug(false);
    const file = e.dataTransfer.files[0];
    if (file && validateFile(file)) { setCustomRugFile(file); setError(''); }
  };

  const currentFile = selectedTab === 0 ? upcQrFile : customRugFile;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Label Printing" category="pdf" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          LABEL PRINTING
        </Typography>

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 0, border: `1px solid ${theme.palette.divider}` }}>
              {/* Tabs */}
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                sx={{
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  '& .MuiTabs-indicator': { backgroundColor: TOOL_COLORS.PDF.primary },
                }}
              >
                <Tab
                  icon={<QrCode sx={{ fontSize: 18 }} />}
                  iconPosition="start"
                  label="UPC / QR Labels"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    '&.Mui-selected': { color: TOOL_COLORS.PDF.primary },
                  }}
                />
                <Tab
                  icon={<Label sx={{ fontSize: 18 }} />}
                  iconPosition="start"
                  label="Custom Rug Labels"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    '&.Mui-selected': { color: TOOL_COLORS.PDF.primary },
                  }}
                />
              </Tabs>

              {/* UPC/QR Labels Tab */}
              {selectedTab === 0 && (
                <Box sx={{ p: 3 }}>
                  {/* Label Type Selection */}
                  <Card sx={{ mb: 3, bgcolor: getCardBackground(theme) }}>
                    <CardContent>
                      <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Label sx={{ color: TOOL_COLORS.PDF.primary }} />
                            Select Label Type
                          </Box>
                        </FormLabel>
                        <RadioGroup
                          value={labelType}
                          onChange={(e) => {
                            setLabelType(e.target.value);
                            if (e.target.value !== 'QR') setAddVendorIdToQR(false);
                          }}
                          sx={{ mt: 1 }}
                        >
                          <FormControlLabel
                            value="QR"
                            control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <QrCode sx={{ color: '#4CAF50' }} />
                                <span>QR Code (For Samples)</span>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            value="Barcode"
                            control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Assignment sx={{ color: '#2196F3' }} />
                                <span>UPC-A Barcode</span>
                              </Box>
                            }
                          />
                          </RadioGroup>

                        {/* Add Vendor ID checkbox */}
                        <FormGroup sx={{ mt: 1, ml: 1 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={addVendorIdToQR}
                                onChange={(e) => setAddVendorIdToQR(e.target.checked)}
                                disabled={labelType !== 'QR'}
                                sx={{
                                  '&.Mui-checked': { color: TOOL_COLORS.PDF.primary },
                                }}
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: labelType !== 'QR' ? theme.palette.text.disabled : theme.palette.text.primary
                                }}
                              >
                                Add Vendor ID to QR code
                              </Typography>
                            }
                          />
                          <Typography
                            variant="caption"
                            sx={{ ml: 4, mt: -0.5, color: theme.palette.text.secondary }}
                          >
                            {addVendorIdToQR
                              ? 'The QR will encode Collection-Vendor ID.'
                              : 'The QR encodes the UPC, or the Item ID when there is no UPC.'}
                          </Typography>
                        </FormGroup>
                      </FormControl>
                    </CardContent>
                  </Card>

                  {/* File Upload */}
                  <Card sx={{ mb: 3, bgcolor: upcQrFile ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Description sx={{ color: TOOL_COLORS.PDF.primary }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                          Import File (Excel/CSV)
                        </Typography>
                        {upcQrFile && (
                          <Tooltip title="File selected">
                            <CheckCircle sx={{ color: '#4CAF50' }} />
                          </Tooltip>
                        )}
                      </Box>

                      {/* Drag and Drop Zone */}
                      <Box
                        component="label"
                        onDragEnter={handleDragEnterUpc}
                        onDragOver={handleDragOverUpc}
                        onDragLeave={handleDragLeaveUpc}
                        onDrop={handleDropUpc}
                        sx={{
                          display: 'block',
                          border: `2px dashed ${isDraggingUpc ? TOOL_COLORS.PDF.primary : upcQrFile ? '#4CAF50' : theme.palette.divider}`,
                          borderRadius: 2,
                          p: 3,
                          textAlign: 'center',
                          bgcolor: isDraggingUpc
                            ? TOOL_COLORS.PDF.light
                            : upcQrFile
                              ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                              : 'transparent',
                          transition: 'all 0.3s ease',
                          mb: 2,
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: upcQrFile ? '#4CAF50' : TOOL_COLORS.PDF.primary,
                            bgcolor: upcQrFile
                              ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                              : TOOL_COLORS.PDF.light,
                          },
                        }}
                      >
                        <input
                          type="file"
                          hidden
                          accept=".xlsx,.xls,.csv"
                          ref={upcQrFileInputRef}
                          onChange={handleUpcQrFileSelect}
                        />
                        {upcQrFile ? (
                          <>
                            <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                              1 file selected
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Click to replace
                            </Typography>
                          </>
                        ) : (
                          <>
                            <CloudUpload sx={{ fontSize: 48, color: isDraggingUpc ? TOOL_COLORS.PDF.primary : 'text.secondary', mb: 1 }} />
                            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                              {isDraggingUpc ? 'Drop file here' : 'Drag & drop file here or click to select'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Excel (.xlsx, .xls) or CSV files only
                            </Typography>
                          </>
                        )}
                      </Box>

                      {upcQrFile && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mt: 1.5,
                            p: 1,
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                            <Description sx={{ color: TOOL_COLORS.PDF.primary, fontSize: 20 }} />
                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {upcQrFile.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                              ({(upcQrFile.size / 1024 / 1024).toFixed(2)} MB)
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={clearUpcFile} sx={{ color: theme.palette.error.main }}>
                            <Clear fontSize="small" />
                          </IconButton>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={downloadUpcQrTemplate}
                          startIcon={<GetApp sx={{ fontSize: '18px !important' }} />}
                          sx={{
                            borderColor: alpha(theme.palette.text.primary, 0.2),
                            color: alpha(theme.palette.text.primary, 0.7),
                            bgcolor: 'transparent',
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 2.5,
                            py: 0.8,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: alpha(theme.palette.text.primary, 0.4),
                              color: theme.palette.text.primary,
                              bgcolor: alpha(theme.palette.text.primary, 0.05),
                            },
                            '&:active': {
                              transform: 'scale(0.98)',
                            },
                          }}
                        >
                          Download Template
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      onClick={() => handleGenerateUpcQrLabels('print')}
                      disabled={!upcQrFile || loading}
                      startIcon={<Print />}
                      sx={{
                        bgcolor: TOOL_COLORS.PDF.primary,
                        '&:hover': { bgcolor: TOOL_COLORS.PDF.hover },
                        px: 4
                      }}
                    >
                      {loading ? 'Generating...' : 'Print Labels'}
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => handleGenerateUpcQrLabels('preview')}
                      disabled={!upcQrFile || loading}
                      startIcon={<Visibility />}
                      sx={{ borderColor: TOOL_COLORS.PDF.primary, color: TOOL_COLORS.PDF.primary }}
                    >
                      Preview
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => handleGenerateUpcQrLabels('download')}
                      disabled={!upcQrFile || loading}
                      startIcon={<GetApp />}
                      sx={{ borderColor: TOOL_COLORS.PDF.primary, color: TOOL_COLORS.PDF.primary }}
                    >
                      Download PDF
                    </Button>

                    {(upcQrFile || results) && (
                      <Button
                        variant="outlined"
                        onClick={resetForm}
                        sx={{
                          borderColor: theme.palette.divider,
                          color: theme.palette.text.primary,
                          '&:hover': {
                            borderColor: theme.palette.text.secondary,
                            bgcolor: alpha(theme.palette.text.primary, 0.05)
                          }
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                </Box>
              )}

              {/* Custom Rug Labels Tab */}
              {selectedTab === 1 && (
                <Box sx={{ p: 3 }}>
                  {/* Label Size Selection */}
                  <Card sx={{ mb: 3, bgcolor: getCardBackground(theme) }}>
                    <CardContent>
                      <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Label sx={{ color: TOOL_COLORS.PDF.primary }} />
                            Select Label Sizes
                          </Box>
                        </FormLabel>
                        <FormGroup row sx={{ gap: 2, mt: 1 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={customRugSizes['2X3']}
                                onChange={() => handleCustomRugSizeChange('2X3')}
                                sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }}
                              />
                            }
                            label="2 X 3 inches"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={customRugSizes['3X5']}
                                onChange={() => handleCustomRugSizeChange('3X5')}
                                sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }}
                              />
                            }
                            label="3 X 5 inches"
                          />
                        </FormGroup>
                      </FormControl>
                    </CardContent>
                  </Card>

                  {/* File Upload */}
                  <Card sx={{ mb: 3, bgcolor: customRugFile ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Description sx={{ color: TOOL_COLORS.PDF.primary }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                          Import Custom Rug Data (Excel/CSV)
                        </Typography>
                        {customRugFile && (
                          <Tooltip title="File selected">
                            <CheckCircle sx={{ color: '#4CAF50' }} />
                          </Tooltip>
                        )}
                      </Box>

                      {/* Drag and Drop Zone */}
                      <Box
                        component="label"
                        onDragEnter={handleDragEnterRug}
                        onDragOver={handleDragOverRug}
                        onDragLeave={handleDragLeaveRug}
                        onDrop={handleDropRug}
                        sx={{
                          display: 'block',
                          border: `2px dashed ${isDraggingRug ? TOOL_COLORS.PDF.primary : customRugFile ? '#4CAF50' : theme.palette.divider}`,
                          borderRadius: 2,
                          p: 3,
                          textAlign: 'center',
                          bgcolor: isDraggingRug
                            ? TOOL_COLORS.PDF.light
                            : customRugFile
                              ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                              : 'transparent',
                          transition: 'all 0.3s ease',
                          mb: 2,
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: customRugFile ? '#4CAF50' : TOOL_COLORS.PDF.primary,
                            bgcolor: customRugFile
                              ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                              : TOOL_COLORS.PDF.light,
                          },
                        }}
                      >
                        <input
                          type="file"
                          hidden
                          accept=".xlsx,.xls,.csv"
                          ref={customRugFileInputRef}
                          onChange={handleCustomRugFileSelect}
                        />
                        {customRugFile ? (
                          <>
                            <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                              1 file selected
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Click to replace
                            </Typography>
                          </>
                        ) : (
                          <>
                            <CloudUpload sx={{ fontSize: 48, color: isDraggingRug ? TOOL_COLORS.PDF.primary : 'text.secondary', mb: 1 }} />
                            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                              {isDraggingRug ? 'Drop file here' : 'Drag & drop file here or click to select'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Excel (.xlsx, .xls) or CSV files only
                            </Typography>
                          </>
                        )}
                      </Box>

                      {customRugFile && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mt: 1.5,
                            p: 1,
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                            <Description sx={{ color: TOOL_COLORS.PDF.primary, fontSize: 20 }} />
                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {customRugFile.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                              ({(customRugFile.size / 1024 / 1024).toFixed(2)} MB)
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={clearRugFile} sx={{ color: theme.palette.error.main }}>
                            <Clear fontSize="small" />
                          </IconButton>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={downloadCustomRugTemplate}
                          startIcon={<GetApp sx={{ fontSize: '18px !important' }} />}
                          sx={{
                            borderColor: alpha(theme.palette.text.primary, 0.2),
                            color: alpha(theme.palette.text.primary, 0.7),
                            bgcolor: 'transparent',
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 2.5,
                            py: 0.8,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: alpha(theme.palette.text.primary, 0.4),
                              color: theme.palette.text.primary,
                              bgcolor: alpha(theme.palette.text.primary, 0.05),
                            },
                            '&:active': {
                              transform: 'scale(0.98)',
                            },
                          }}
                        >
                          Download Template
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Process Button */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={handleGenerateCustomRugLabels}
                      disabled={!customRugFile || Object.values(customRugSizes).every(v => !v) || loading}
                      sx={{
                        bgcolor: TOOL_COLORS.PDF.primary,
                        '&:hover': { bgcolor: TOOL_COLORS.PDF.hover },
                        px: 4
                      }}
                    >
                      {loading ? 'Generating Labels...' : 'Generate Custom Rug Labels'}
                    </Button>

                    {(customRugFile || results) && (
                      <Button
                        variant="outlined"
                        onClick={resetForm}
                        sx={{
                          borderColor: theme.palette.divider,
                          color: theme.palette.text.primary,
                          '&:hover': {
                            borderColor: theme.palette.text.secondary,
                            bgcolor: alpha(theme.palette.text.primary, 0.05)
                          }
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Status & Info Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Downloads
              </Typography>

              {/* Progress */}
              {loading && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                    Generating labels...
                  </Typography>
                  <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: TOOL_COLORS.PDF.primary } }} />
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {success}
                </Alert>
              )}

              {/* Results Summary */}
              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                    Processing Results:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <Label sx={{ color: TOOL_COLORS.PDF.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${results.labelType} Labels Generated`}
                        secondary="Labels created successfully"
                        primaryTypographyProps={{ color: theme.palette.text.primary }}
                        secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Assignment sx={{ color: TOOL_COLORS.PDF.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`File: ${results.fileName}`}
                        secondary="Successfully processed and downloaded"
                        primaryTypographyProps={{ color: theme.palette.text.primary }}
                        secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                      />
                    </ListItem>
                  </List>
                </Box>
              )}

              {results && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {results.mode === 'print'
                      ? 'Print dialog opened. Select your printer and click Print.'
                      : 'Labels have been automatically downloaded. Check your downloads folder.'}
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Current Configuration */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                Current Configuration:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                <Chip
                  label={selectedTab === 0 ? `Type: ${labelType === 'QR' ? 'QR Code' : labelType === 'Barcode' ? 'UPC-A' : 'No Code'}` : 'Custom Rug Labels'}
                  size="small"
                  sx={{ borderColor: TOOL_COLORS.PDF.primary, color: TOOL_COLORS.PDF.primary }}
                  variant="outlined"
                />
                {selectedTab === 1 && Object.entries(customRugSizes).filter(([, v]) => v).map(([size]) => (
                  <Chip
                    key={size}
                    label={`Size: ${size}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
                {currentFile && (
                  <Chip
                    label={`File: ${currentFile.name.length > 20 ? currentFile.name.substring(0, 20) + '...' : currentFile.name}`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Box>

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                Instructions:
              </Typography>
              <Typography variant="body2" component="div" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8 }}>
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>1.</Box> Select label tab (UPC/QR or Custom Rug)<br />
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>2.</Box> Choose label type or sizes<br />
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>3.</Box> Download template and populate data<br />
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>4.</Box> Upload populated Excel/CSV file<br />
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>5.</Box> Generate and download labels
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* ── Label preview ── */}
      <Dialog
        open={!!preview}
        onClose={closePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Visibility sx={{ color: TOOL_COLORS.PDF.primary }} />
          <Box>
            <Typography variant="h6" component="div">Label preview</Typography>
            {preview && (
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {preview.labelType} &middot; {preview.fileName}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5' }}>
          {preview && (
            <iframe
              ref={previewFrameRef}
              title="Label preview"
              src={preview.url}
              style={{ width: '100%', height: '100%', minHeight: '60vh', border: 'none', display: 'block' }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Typography variant="caption" sx={{ mr: 'auto', color: theme.palette.text.secondary }}>
            Check the layout and codes before printing. Printing or downloading from here uses this
            same file &mdash; nothing is generated again.
          </Typography>
          <Button onClick={closePreview}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<GetApp />}
            onClick={downloadPreview}
            sx={{ borderColor: TOOL_COLORS.PDF.primary, color: TOOL_COLORS.PDF.primary }}
          >
            Download PDF
          </Button>
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={printPreview}
            sx={{ bgcolor: TOOL_COLORS.PDF.primary, '&:hover': { bgcolor: TOOL_COLORS.PDF.hover } }}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LabelPrinting;
